import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneIncoming, Siren } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface IncomingCallBannerProps {
  call: {
    name: string;
    initials: string;
    colorLight: string;
    colorDark: string;
    group: string;
    isEmergency?: boolean;
  };
  onTap: () => void;
  onDismiss: () => void;
}
export function IncomingCallBanner({
  call,
  onTap,
  onDismiss
}: IncomingCallBannerProps) {
  const {
    isLight
  } = useTheme();
  const [visible, setVisible] = useState(false);
  const isEmergency = Boolean(call.isEmergency);
  // Deliberate delay before showing — feels like a real incoming call notification
  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), isEmergency ? 400 : 900);
    return () => clearTimeout(showTimer);
  }, [isEmergency]);
  // Auto-dismiss after visible — emergencies linger a little longer
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => onDismiss(), isEmergency ? 7000 : 5000);
    return () => clearTimeout(timer);
  }, [visible, onDismiss, isEmergency]);
  if (!visible) return null;
  return <motion.div initial={{
    opacity: 0,
    y: -80,
    scale: 0.92,
    filter: 'blur(8px)'
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)'
  }} exit={{
    opacity: 0,
    y: -60,
    scale: 0.95,
    filter: 'blur(4px)'
  }} transition={{
    type: 'spring',
    stiffness: 220,
    damping: 26,
    mass: 1
  }} className="absolute top-4 left-3 right-3 z-[60] cursor-pointer" onClick={onTap} data-id="element-651" data-mp="2p2cl56u24qqx">
      <motion.div initial={{
      boxShadow: isEmergency ? '0 0 0 0 rgba(239, 68, 68, 0)' : '0 0 0 0 rgba(0, 200, 83, 0)'
    }} animate={{
      boxShadow: isEmergency ? ['0 0 0 0 rgba(239, 68, 68, 0.28)', '0 0 0 10px rgba(239, 68, 68, 0)'] : ['0 0 0 0 rgba(0, 200, 83, 0.15)', '0 0 0 8px rgba(0, 200, 83, 0)']
    }} transition={{
      duration: isEmergency ? 1.2 : 1.8,
      repeat: Infinity,
      ease: 'easeOut'
    }} className={`relative overflow-hidden rounded-2xl shadow-2xl border ${isEmergency ? isLight ? 'bg-white border-red-200 shadow-red-900/10' : 'bg-[#1a1212] border-red-500/25 shadow-black/60' : isLight ? 'bg-white border-slate-200 shadow-black/10' : 'bg-[#1a1a1a] border-white/10 shadow-black/60'}`} data-id="element-652" data-mp="32t7xit3rlpz9">
        {/* Emergency ribbon */}
        {isEmergency && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className={`flex items-center gap-1.5 px-3.5 py-1.5 ${isLight ? 'bg-red-600' : 'bg-red-600/90'}`} data-id="element-653" data-mp="156f6wnocmfu7">
            <motion.span animate={{
          opacity: [1, 0.35, 1]
        }} transition={{
          duration: 1.1,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="flex items-center gap-1.5" data-id="element-654" data-mp="ckqire6l3nrn">
              <Siren className="h-3 w-3 text-white" data-id="element-655" data-mp="mjg0n6yhd87w" />
              <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-white" data-id="element-656" data-mp="hi8ufcbzkh3c">
                Emergency call
              </span>
            </motion.span>
          </motion.div>}

        <div className="relative flex items-center gap-3.5 p-3.5" data-id="element-657" data-mp="qjzxsaj20bu1">
          {/* Animated left accent */}
          <motion.div initial={{
          height: 0
        }} animate={{
          height: '100%'
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: 'easeOut'
        }} className={`absolute left-0 top-0 w-[3px] rounded-full ${isEmergency ? 'bg-red-500' : 'bg-vexu'}`} data-id="element-658" data-mp="158mfgiw5lfof" />

          {/* Avatar with subtle entrance */}
          <motion.div initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.15,
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${isLight ? call.colorLight : call.colorDark}`} data-id="element-659" data-mp="33o286lgkmmq4">
            {call.initials}
          </motion.div>

          <div className="flex-1 min-w-0" data-id="element-660" data-mp="2unx68qu5cgjc">
            <motion.div initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} className="flex items-center gap-2" data-id="element-661" data-mp="2yqtscriwaus1">
              <h4 className={`text-[14px] font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-662" data-mp="1ykq025t804rm">
                {isEmergency ? `Emergency call from ${call.name}` : call.name}
              </h4>
              {!isEmergency && <span className="flex h-2 w-2 relative" data-id="element-663" data-mp="1kqyybjlqdp6g">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-vexu opacity-75" data-id="element-664" data-mp="1fsx72be5ijjw"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-vexu" data-id="element-665" data-mp="1as9kc1wygovh"></span>
                </span>}
            </motion.div>
            <motion.p initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3,
            duration: 0.4
          }} className={`text-xs truncate mt-0.5 ${isEmergency ? isLight ? 'font-semibold text-red-600' : 'font-semibold text-red-400' : isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-666" data-mp="11smff650p0xo">
              {isEmergency ? 'Urgent — tap to listen in now' : 'AI Agent is handling this call...'}
            </motion.p>
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.5
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.35,
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isEmergency ? isLight ? 'bg-red-100 text-red-600' : 'bg-red-500/15 text-red-400' : isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-667" data-mp="3b7kmjqsa45kg">
            {isEmergency ? <Siren className="w-4 h-4" data-id="element-668" data-mp="2b0pf14tbdufx" /> : <PhoneIncoming className="w-4 h-4" data-id="element-669" data-mp="2b3bml4g7g9y5" />}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>;
}